<template>
  <div v-if="loading">
    <loadingAnimation />
  </div>
  <div v-else-if="storyNotFound">
    <div class="row m-5 p-5">
      <div class="col text-center">
        <h1>Hmmm, something went wrong</h1>
        <p>
          We couldn't find a story with that title <br />
          Check your URL and try again.
        </p>
      </div>
    </div>
  </div>
  <div v-else="!loading" class="ctpx-common-thread ctpx-common-thread-story">
    <!-- hero img -->
    <div class="story-intro position-relative">
      <!-- if image -->
      <div
        v-if="bgUrl && !videoHeaderUrl"
        class="hero"
        :style="{ backgroundImage: `url(${bgUrl})` }"
      ></div>

      <!-- if video -->
      <div
        v-else-if="videoHeaderUrl"
        class="video-header"
        v-on:click="toggleTitle"
      >
        <VideoPlayer :options="videoOptionsHero" />
      </div>

      <div class="story-title position-absolute" v-show="showTitle">
        <h1 class="display-1">{{ title }}</h1>
      </div>
    </div>
    <!-- content block-->
    <div class="container content-container">
      <div class="row">
        <div class="col-md-10 offset-md-1 d-flex justify-content-between mb-3">
          <!-- breadcrumb -->
          <div class="breadcrumb">
            <p>
              <a href="/pages/common-thread-archive">Stories</a>
              /
              <a :href="'/pages/common-thread-archive?category=' + category">{{
                category
              }}</a>
            </p>
          </div>
          <!-- author | date -->
          <div class="auth-date">
            <p>
              <span v-for="(name, index) in authors"
                ><span>{{ name }}</span
                ><span v-if="index + 1 < authors.length">, </span></span
              >
              <span v-if="authors.length > 0"> |</span> {{ formatDateString(date) }}
            </p>
          </div>
        </div>
      </div>
      <!--.row-->
      <!-- <div v-if="videoOptions.sources[0].src">
        <VideoPlayer :options="videoOptions" />
      </div> -->

      <!-- article body -->
      <div class="row">
        <div
          v-for="component in articleBody"
          class="col-md-10 offset-md-1 articleBody"
        >
          <!-- wysiwyg text field -->
          <div
            v-if="
              component.__component === ArticleComponent.wysiwyg &&
              component.bodyCopy
            "
          >
            <span v-html="component.bodyCopy"></span>
          </div>
          <!-- video upload -->
          <div
            v-if="
              component.__component === ArticleComponent.videoUpload &&
              component.videoUpload.data
            "
          >
            <video
              class="video-js"
              controls
              preload="auto"
              :class="component.orientation"
            >
              <source
                :src="component.videoUpload.data.attributes.url"
                :type="component.videoUpload.data.attributes.mime"
              />
              <p class="vjs-no-js">
                To view this video please enable JavaScript, and consider
                upgrading to a web browser that
                <a
                  href="https://videojs.com/html5-video-support/"
                  target="_blank"
                  >supports HTML5 video</a
                >
              </p>
            </video>
          </div>
          <!-- video embed -->
          <div
            v-if="
              component.__component === ArticleComponent.videoEmbed &&
              component.source &&
              component.videoId
            "
            class="iframe-container my-3"
          >
            <iframe
              v-if="component.source === 'YouTube'"
              frameborder="0"
              loading="lazy"
              :src="`https://www.youtube.com/embed/${component.videoId}`"
            ></iframe>
            <iframe
              v-else-if="component.source === 'Vimeo'"
              :src="`https://player.vimeo.com/video/${component.videoId}`"
              frameborder="0"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            ></iframe>
          </div>
          <!-- inline photo -->
          <div
            v-if="
              component.__component === ArticleComponent.inlinePhoto &&
              component.articlePhoto.data
            "
            :class="component.orientation"
          >
            <figure>
              <img :src="component.articlePhoto.data.attributes.url" />
              <figcaption v-if="component.articlePhoto.data.attributes.caption">
                {{ component.articlePhoto.data.attributes.caption }}
              </figcaption>
            </figure>
          </div>
          <!-- quote -->
          <div
            v-if="
              component.__component === ArticleComponent.blockQuote &&
              component.quote
            "
          >
            <blockquote class="blockquote">
              &ldquo;
              <p>{{ component.quote }}</p>
              &rdquo;
              <footer class="blockquote-footer">
                <cite :title="component.cite">{{ component.cite }}</cite>
              </footer>
            </blockquote>
          </div>
          <!-- featured products header -->
          <div
            v-if="
              component.__component === ArticleComponent.productHeading &&
              component.featuredProductsHeader
            "
            class="text-center prod-header"
          >
            <h2>{{ component.featuredProductsHeader }}</h2>
          </div>
          <!--products-->
          <div v-if="component.__component === ArticleComponent.product">
            <div
              v-if="component.productData && component.productData.length === 1"
              class="singleProductCard"
            >
              <singleProductCard :product="component.productData[0]" />
            </div>
            <div
              v-if="component.productData && component.productData.length > 1"
              class="product-suggest"
            >
              <!--.mobile-swiper-->
              <div class="row mobile-only mobile-swiper">
                <swiper
                  :modules="modules"
                  :slides-per-view="1"
                  :centeredSlides="true"
                  :space-between="10"
                  :lazy="true"
                  :loop="true"
                  navigation
                  @swiper="onSwiper"
                >
                  <template v-for="product in component.productData">
                    <swiper-slide>
                      <productCard :product="product" />
                    </swiper-slide>
                  </template>
                </swiper>
              </div>
              <div class="row hidden-mobile">
                <template v-for="product in component.productData">
                  <div class="col-6 col-md-4">
                    <productCard :product="product" />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-5">
        <div
          class="col-md-10 offset-md-1 d-flex justify-content-between mb-3 pointer"
        >
          <p @click="goBack()" class="mb-0">
            <span class="pe-1"><strong>&lt;</strong></span
            >Back
          </p>

          <div class="sharethis-custom">
            <span class="title">Share</span>
            <div class="st-custom-button" data-network="pinterest">
              <img
                alt="pinterest sharing button"
                src="https://platform-cdn.sharethis.com/img/pinterest.svg"
              />
              <span class="st-label">Pin</span>
            </div>
            <div class="st-custom-button" data-network="twitter">
              <img
                alt="twitter sharing button"
                src="https://platform-cdn.sharethis.com/img/twitter.svg"
              />
              <span class="st-label">Tweet</span>
            </div>
            <div class="st-custom-button" data-network="facebook">
              <img
                alt="facebook sharing button"
                src="https://platform-cdn.sharethis.com/img/facebook.svg"
              />
              <span class="st-label">Share</span>
            </div>
            <div class="st-custom-button st-last" data-network="sharethis">
              <img
                alt="sharethis sharing button"
                src="https://platform-cdn.sharethis.com/img/sharethis.svg"
              />
              <span class="st-label">Share</span>
            </div>
          </div>
        </div>
        <!--.col-md-10-->
      </div>
      <!--.row-->
    </div>
    <div class="container-fluid" v-if="relatedStories.length">
      <div class="ctpx-short-sec more-stories">
        <div class="row">
          <div class="col">
            <h3 class="display-hero-sub lead">More Stories You'll Love</h3>
          </div>
          <!--.col-->
        </div>
        <!--.row-->
        <!-- Related Stories (mobile) -->
        <div class="mobile-only mobile-swiper row">
          <swiper
            :modules="modules"
            :slides-per-view="'auto'"
            :centeredSlides="true"
            :space-between="10"
            :pagination="{ clickable: true }"
            :lazy="true"
            :loop="true"
            @swiper="onSwiper"
          >
            <template v-for="story in relatedStories" :key="story.id">
              <swiper-slide
                ><storyCard
                  :storyId="story.id"
                  :storyDetails="story.attributes"
              /></swiper-slide>
            </template>
          </swiper>
        </div>

        <!-- Related Stories (desktop) -->
        <div class="row hidden-mobile">
          <template v-for="story in relatedStories" :key="story.id">
            <storyCard :storyId="story.id" :storyDetails="story.attributes" />
          </template>
        </div>
      </div>
      <!--.ctpx-short-sec-->
    </div>
    <!-- spotify playlist -->
    <spotify
      v-if="spotifyPlacement === SpotifyPlacement.allPages"
      :spotifyTitle="spotifyTitle"
      :playlistLink="spotifyLink"
      :bgImg="spotifyImg"
      :description="spotifyDesc"
    />

    <!-- g4gBlock -->
    <g4gBlock />
  </div>
</template>

<script src="./ctrl-common-thread-story.js"></script>
